/**
 * Minimal YAML serializer for ACR export models. Extracted so both the single-file YAML download
 * and the bulk zip export produce byte-for-byte identical YAML from the same model.
 */

function escapeYamlString(value) {
  const str = String(value ?? "");
  return `'${str.replace(/'/g, "''")}'`;
}

function shouldQuoteYamlString(value) {
  if (value === "") return true;
  // Keep plain scalars for normal filled values (e.g., "web"), quote only when needed.
  if (/^\s|\s$/.test(value)) return true;
  if (/[:#[\]{}&,*!|>'"%@`]/.test(value)) return true;
  if (/\n/.test(value)) return true;
  const lower = value.toLowerCase();
  if (["true", "false", "null", "~"].includes(lower)) return true;
  if (/^[+-]?\d+(\.\d+)?$/.test(value)) return true;
  return false;
}

function isScalar(value) {
  return value == null || ["string", "number", "boolean"].includes(typeof value);
}

function scalarToYaml(value) {
  if (value == null) return "''";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  const str = String(value);
  return shouldQuoteYamlString(str) ? escapeYamlString(str) : str;
}

function toYamlLines(value, depth = 0) {
  const indent = "  ".repeat(depth);
  if (Array.isArray(value)) {
    if (!value.length) return [`${indent}[]`];
    return value.flatMap((item) => {
      if (isScalar(item)) return [`${indent}- ${scalarToYaml(item)}`];
      if (item && typeof item === "object") {
        const entries = Object.entries(item);
        if (!entries.length) return [`${indent}- {}`];
        const [firstKey, firstValue] = entries[0];
        const lines = [];
        if (isScalar(firstValue)) {
          lines.push(`${indent}- ${firstKey}: ${scalarToYaml(firstValue)}`);
        } else if (Array.isArray(firstValue) && firstValue.length === 0) {
          lines.push(`${indent}- ${firstKey}: []`);
        } else {
          lines.push(`${indent}- ${firstKey}:`);
          lines.push(...toYamlLines(firstValue, depth + 2));
        }

        entries.slice(1).forEach(([key, entryValue]) => {
          if (isScalar(entryValue)) {
            lines.push(`${"  ".repeat(depth + 1)}${key}: ${scalarToYaml(entryValue)}`);
            return;
          }
          if (Array.isArray(entryValue) && entryValue.length === 0) {
            lines.push(`${"  ".repeat(depth + 1)}${key}: []`);
            return;
          }
          lines.push(`${"  ".repeat(depth + 1)}${key}:`);
          lines.push(...toYamlLines(entryValue, depth + 2));
        });
        return lines;
      }
      return [`${indent}- ${scalarToYaml(item)}`];
    });
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) return [`${indent}{}`];
    const lines = [];
    entries.forEach(([key, entryValue]) => {
      if (isScalar(entryValue)) {
        lines.push(`${indent}${key}: ${scalarToYaml(entryValue)}`);
        return;
      }
      if (Array.isArray(entryValue) && entryValue.length === 0) {
        lines.push(`${indent}${key}: []`);
        return;
      }
      lines.push(`${indent}${key}:`);
      lines.push(...toYamlLines(entryValue, depth + 1));
    });
    return lines;
  }

  return [`${indent}${scalarToYaml(value)}`];
}

export default function serializeAcrYaml(value, depth = 0) {
  return toYamlLines(value, depth).join("\n");
}
